import MojitoServices from 'mojito/services';

import {mergeToHierarchy} from '#core/utils/config-utils.js';
import {PROXY_SHOW_DEPOSIT} from '#core/utils/intent-types.js';

import {AbstractVisualFeature} from '#core/application/modules/visual-feature.js';
import {Bonuses} from '#core/application/modules/bonuses/bonuses.js';

import {BettingCenter} from '../betting-center.js';

import BETSLIP_NOTIFICATIONS_MAP from './betslip-notifications-map.json';
import {betslipFactory} from './betslip.factory';

import SCHEMA from './betslip.schema.yaml';
import PALETTE_SCHEMA from './betslip.palette-schema.yaml';

class BetslipFeature extends AbstractVisualFeature {
    get name() {
        return 'Betslip';
    }

    get configSchema() {
        return SCHEMA;
    }

    get paletteSchema() {
        return PALETTE_SCHEMA;
    }

    getVisualConfig(PALETTE) {
        return betslipFactory(PALETTE);
    }

    beforeMojitoConfigBuild(mojitoConfig) {
        const config = this.config;
        const {numericKeypad, showTaxAbsorptionMessage, settings, selectionsCount, openBetsCount} = config;
        const {automaticPriceChangeAcceptance} = settings;

        mergeToHierarchy(mojitoConfig, 'services.betslipService', {
            service: MojitoServices.Betslip.service,
            // Disabled for GenBet integration as it is not supported there.
            refreshBonusOffersInterval: IS_LOTTO || GENBET_BASED ? 0 : Bonuses.refreshBonusOffersInterval * 1000,
            refreshOveraskStatusInterval: config.refreshOveraskStatusInterval * 1000,
            refreshOveraskStatusDelay: config.refreshOveraskStatusDelay * 1000,
            enableBonusCall: Bonuses.doSeparateBonusCall,
        });

        mergeToHierarchy(mojitoConfig, 'services.stores.betslip', {
            storageTimeInHours: config.storageTimeInHours,
            allowBetPlacement: config.allowBetPlacement,

            // Disabled for GenBet integration as it is not supported there.
            enableBonusValidation: !GENBET_BASED && Bonuses.doBonusValidation,
        });

        mergeToHierarchy(mojitoConfig, 'services.configurations', {
            BetBuilder: {
                showBetslipSettings: settings.showSettings,
                showTeasers: config.showTeasers,
                showBankers: config.showBankers,
                showGroupedSingles: config.showGroupedSingles ?? !GENBET_BASED,
            },
            YourBetsController: {
                showSettledBetsTab: GENBET_BASED,
                showSettledBetsOnBoarding: GENBET_BASED,
            },
            Betslip: {
                showBetslipSettingsInEmptyBetslip: settings.showSettings && settings.showSettingsInEmptyBetslip,
            },
            BettingSettings: {
                _mobile_enableQuickBetslipConfiguration: settings.enableQuickBetslipConfiguration,
                automaticPriceChangeAcceptance: {
                    configurable: {
                        all: automaticPriceChangeAcceptance.all,
                        higher: automaticPriceChangeAcceptance.higher,
                        lower: automaticPriceChangeAcceptance.lower,
                    },
                },
            },
            BetslipNotifications: {
                showDepositOnInsufficientFunds: config.showDepositOnInsufficientFunds,
                notificationsMap: BETSLIP_NOTIFICATIONS_MAP,
                depositIntent: PROXY_SHOW_DEPOSIT,
            },
            NumericKeypad: {
                incrementalNumericKeypadWide: {
                    increaseButtons: {
                        values: {
                            key1: numericKeypad.key1,
                            key2: numericKeypad.key2,
                            key3: numericKeypad.key3,
                            key4: numericKeypad.key4,
                        },
                    },
                },
                incrementalNumericKeypadNarrow: {
                    increaseButtons: {
                        values: {
                            key1: numericKeypad.key1,
                            key2: numericKeypad.key2,
                            key3: numericKeypad.key3,
                            key4: numericKeypad.key4,
                        },
                    },
                },
            },
            CompactNumericKeypad: {
                incrementalView: {
                    increaseButtons: {
                        values: {
                            key1: numericKeypad.key1,
                            key2: numericKeypad.key2,
                            key3: numericKeypad.key3,
                            key4: numericKeypad.key4,
                        },
                    },
                },
            },
            TaxAbsorptionDisclaimer: {
                hide: !showTaxAbsorptionMessage,
            },
            BettingCenterView: {
                selectionsCount: {
                    hidden: !selectionsCount.showSelectionsCount,
                    displayWhenEmpty: selectionsCount.showSelectionsCountWhenEmpty,
                },
                openBetsCount: {
                    hidden: !openBetsCount.showOpenBetsCount,
                    displayWhenEmpty: openBetsCount.showOpenBetsCountWhenEmpty,
                },
            },
        });

        return super.beforeMojitoConfigBuild(mojitoConfig);
    }
}

export const Betslip = new BetslipFeature(BettingCenter);
